import { Suspense, useMemo, lazy } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useParams, Navigate } from "react-router-dom";
import Footer from "@/components/Footer";
import { getPostBySlug, BlogPost as BlogPostType } from "@/lib/blog";
import { slugToFilename } from "@/lib/blogFilenameMap";
// SEO: Replaced Helmet with shared SEO component for consistent metadata and schema
import SEO from "@/components/SEO";
import { MdxComponentsProvider } from "@/components/mdx/MdxComponents";
import AboutAuthor from "@/components/AboutAuthor";
import { translations } from "@/translations/translations";
import Breadcrumbs from "@/components/Breadcrumbs";

// Use import.meta.glob to load all MDX files
// Changed to lazy loading for performance optimization
// Note: This may cause hydration mismatch (Error #418) which is accepted for performance gains
const mdxPosts = import.meta.glob('../content/blog/*.mdx');

import { getKeywordsForPost } from "@/lib/blogSeo";

const BlogPostContent = ({ slug }: { slug: string }) => {
  // Hooks must be called unconditionally at the top level
  // Use the mapping to get the correct filename
  const filename = slugToFilename[slug] || slug;
  const filePath = slug ? `../content/blog/${filename}.mdx` : "";

  const Content = useMemo(() => {
    if (!filePath) return null;
    const loader = mdxPosts[filePath] as () => Promise<{ default: React.ComponentType }>;
    if (!loader) return null;
    return lazy(loader);
  }, [filePath]);

  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  const foundPost = getPostBySlug(slug);

  if (!foundPost) {
    console.error('No post found for slug:', slug);
    return <Navigate to="/blog" replace />;
  }

  if (!Content) {
    console.error('MDX file not found in glob:', filePath);
    // In production this might happen if a file is deleted but the slug remains in a list somehow
    return <Navigate to="/blog" replace />;
  }

  const post = {
    ...foundPost,
    content: Content
  };

  // SEO: Generate localized keywords for the blog post by combining standard OD keywords with post title words
  // This ensures we target "Organizational Development Heilbronn" (or German equivalent) + specific article topics
  const postKeywords = getKeywordsForPost(post);

  return (
    <>
      {/* SEO: Using shared component to ensure ProfessionalService schema + Article schema */}
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={postKeywords}
        image={post.image}
        imageAlt={post.title}
        url={`https://rassner-dev.org/blog/${slug}`}
        type="article"
        publishedTime={post.date}
        dateModified={post.lastUpdated}
        author="Kevin Rassner"
        language={post.language}
        disableLanguageParam={true}
      />

      <main id="main-content" className="container mx-auto px-2 sm:px-4 py-12">
        <Breadcrumbs />

        <div className="max-w-4xl mx-auto">
          <article className="max-w-none bg-card/50 backdrop-blur-sm border border-primary/20 rounded-lg p-4 sm:p-6 md:p-12">
            <div className="mb-8 pb-8 border-b border-border">
              {post.image && (
                <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
                  {/* SEO: Added explicit dimensions and fetch priority to prevent CLS and improve LCP for blog posts */}
                  <img
                    src={post.image}
                    alt={post.title}
                    width={1200}
                    height={630}
                    fetchPriority="high"
                    className="w-full h-auto object-cover max-h-[400px] md:max-h-[500px]"
                  />
                </div>
              )}
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{post.title}</h1>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span>{post.date}</span>
                {post.isLinkedInPost && post.linkedinUrl && (
                  <a
                    href={post.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1 bg-transparent border-none p-0 cursor-pointer text-sm"
                    title="View on LinkedIn"
                  >
                    View on LinkedIn
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>

            <div className="prose prose-lg prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-a:text-primary prose-code:text-foreground prose-pre:bg-muted prose-blockquote:text-foreground/80">
              <MdxComponentsProvider>
                {Content && (
                  <Suspense fallback={<div className="h-96 flex items-center justify-center">Loading content...</div>}>
                    <Content />
                  </Suspense>
                )}
              </MdxComponentsProvider>
            </div>

            <AboutAuthor language={post.language} />
          </article>
        </div>
      </main>
    </>
  );
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Suspense fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      }>
        <BlogPostContent slug={slug} />
      </Suspense>
      <Footer />
    </div>
  );
};

export default BlogPost;
